import { Routes, Route, Link } from 'react-router-dom'
import Layout from './components/Layout'
import Landing from './pages/Landing'
import Dashboard from './pages/Dashboard'
import AuthPage from './pages/AuthPage'
import NotFound from './pages/NotFound'
import { AuthProvider, useAuth } from './auth'
import { lazy, Suspense, useEffect } from 'react'
import { useLocale } from './locale'
import { getMyPreferences, getFxRates } from './api'
import { Check } from '@phosphor-icons/react'
import BootScreen from './components/BootScreen'
import TourProvider from './components/Tour/TourProvider'
import { useTourStore } from './components/Tour/useTourStore'

// Code-split everything off the critical path (landing, dashboard, auth).
// Heavy pages (recharts, dagre) only load when their route is visited.
//
// After a deploy, content-hashed chunks from the previous build 404 for users
// mid-session. One automatic reload picks up the new build; the sessionStorage
// guard prevents a reload loop if the import keeps failing for another reason.
const CHUNK_RELOAD_KEY = 'lo-chunk-reload'

function lazyWithReload(importer: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    importer().then(
      (mod) => {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY)
        return mod
      },
      (err) => {
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
          window.location.reload()
          return new Promise<never>(() => {}) // render nothing while reloading
        }
        throw err
      },
    ),
  )
}

const DecisionBuilder = lazyWithReload(() => import('./pages/DecisionBuilder'))
const Results = lazyWithReload(() => import('./pages/Results'))
const DecisionAgent = lazyWithReload(() => import('./pages/DecisionAgent'))
const HowItWorks = lazyWithReload(() => import('./pages/HowItWorks'))
const HowTo = lazyWithReload(() => import('./pages/HowTo'))
const SharePage = lazyWithReload(() => import('./pages/SharePage'))
const Explore = lazyWithReload(() => import('./pages/Explore'))
const Compare = lazyWithReload(() => import('./pages/Compare'))
const ForgotPassword = lazyWithReload(() => import('./pages/ForgotPassword'))
const ResetPassword = lazyWithReload(() => import('./pages/ResetPassword'))
const EmailSettings = lazyWithReload(() => import('./pages/EmailSettings'))
const CheckinOutcome = lazyWithReload(() => import('./pages/CheckinOutcome'))
const AccountSettings = lazyWithReload(() => import('./pages/AccountSettings'))
const PricingPage = lazyWithReload(() => import('./pages/PricingPage'))
const Privacy = lazyWithReload(() => import('./pages/Privacy'))
const Terms = lazyWithReload(() => import('./pages/Terms'))
const Imprint = lazyWithReload(() => import('./pages/Imprint'))
const Support = lazyWithReload(() => import('./pages/Support'))
const About = lazyWithReload(() => import('./pages/About'))
const Methodology = lazyWithReload(() => import('./pages/Methodology'))
const DecisionTemplatePage = lazyWithReload(() => import('./pages/DecisionTemplatePage'))
const DecisionsIndex = lazyWithReload(() => import('./pages/DecisionsIndex'))
const Blog = lazyWithReload(() => import('./pages/Blog'))
const BlogPost = lazyWithReload(() => import('./pages/BlogPost'))
const ShouldIQuit = lazyWithReload(() => import('./pages/ShouldIQuit'))
const QuitPlaybook = lazyWithReload(() => import('./pages/QuitPlaybook'))
const WhereCanIRetire = lazyWithReload(() => import('./pages/WhereCanIRetire'))
const AdminLayout = lazyWithReload(() => import('./pages/admin/AdminLayout'))
const Overview = lazyWithReload(() => import('./pages/admin/Overview'))
const AdminUsers = lazyWithReload(() => import('./pages/admin/Users'))
const AdminPayments = lazyWithReload(() => import('./pages/admin/Payments'))
const AdminShares = lazyWithReload(() => import('./pages/admin/Shares'))

function UpgradeSuccess() {
  return (
    <div className="max-w-md mx-auto py-24 px-4 text-center space-y-6">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border-2 border-primary">
        <Check size={32} weight="bold" className="text-primary-ink" />
      </div>
      <h1 className="font-heading font-black text-3xl tracking-tight">You're Premium!</h1>
      <p className="text-ink-muted">
        Welcome to LifeOdds Premium. You now have access to up to 10,000 simulations and up to 10 choices per decision.
      </p>
      <Link
        to="/app"
        className="inline-flex items-center gap-2 bg-primary text-white border-2 border-black font-bold px-6 py-3 shadow-[4px_4px_0_black] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0_black] transition-all"
      >
        Go to dashboard
      </Link>
    </div>
  )
}

/** Fetch the latest EUR-based FX rates once per app boot (server caches 24h). */
function FxBootstrap() {
  const setFx = useLocale((s) => s.setFx)
  useEffect(() => {
    getFxRates('EUR')
      .then((r) => setFx(r.rates, r.as_of))
      .catch(() => { /* leave persisted defaults */ })
  }, [setFx])
  return null
}

/** Tiny gateway: when an authed user lands, fetch their server-side preferences
 * once and sync locale + tour-completion state with whatever they have saved. */
function PreferencesSync() {
  const auth = useAuth()
  const setCode = useLocale((s) => s.setCode)
  const skipTour = useTourStore((s) => s.skip)
  useEffect(() => {
    if (!auth.user) return
    getMyPreferences()
      .then((prefs) => {
        const valid = ['EUR_DE','EUR_FR','GBP_UK','CHF_CH','NOK_NO','SEK_SE','USD_US']
        if (prefs?.locale && valid.includes(prefs.locale)) {
          setCode(prefs.locale as any)
        }
        // If the user finished the tour on another device, mark it complete locally
        if (prefs?.tour_completed) {
          skipTour()
        }
      })
      .catch(() => { /* non-critical */ })
  }, [auth.user, setCode, skipTour])
  return null
}

function AppShell() {
  const { user } = useAuth()
  const localeCode = useLocale((s) => s.code)

  // user === undefined means auth check is still in flight
  if (user === undefined) return <BootScreen />

  return (
    <Layout key={localeCode}>
      <FxBootstrap />
      <PreferencesSync />
      <TourProvider />
      <Suspense fallback={<BootScreen />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/app" element={<Dashboard />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/signup" element={<AuthPage mode="signup" />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/settings/email" element={<EmailSettings />} />
        <Route path="/checkin/:token" element={<CheckinOutcome />} />
        <Route path="/settings/account" element={<AccountSettings />} />
        <Route path="/decision/:id" element={<DecisionBuilder />} />
        <Route path="/results/:id" element={<Results />} />
        <Route path="/agent" element={<DecisionAgent />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/how-to" element={<HowTo />} />
        <Route path="/share/:id" element={<SharePage />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/imprint" element={<Imprint />} />
        <Route path="/support" element={<Support />} />
        <Route path="/about" element={<About />} />
        <Route path="/methodology" element={<Methodology />} />
        <Route path="/decisions" element={<DecisionsIndex />} />
        <Route path="/decisions/:slug" element={<DecisionTemplatePage />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/should-i-quit" element={<ShouldIQuit />} />
        <Route path="/should-i-quit/playbook" element={<QuitPlaybook />} />
        <Route path="/where-can-i-retire" element={<WhereCanIRetire />} />
        <Route path="/upgrade/success" element={<UpgradeSuccess />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Overview />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="shares" element={<AdminShares />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </Layout>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  )
}
